import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { useAuthStore } from '@vesper/shared';
import { Toaster } from 'sonner';
import AppLayout from './components/layout/AppLayout';
import ErrorBoundary from './components/ErrorBoundary';
import { useOnlineStatus } from './hooks/useOnlineStatus';
import { OfflineBanner } from './components/OfflineBanner';

import UploadIndicator from './components/UploadIndicator';
import { DemoSurfaceTracker } from './lib/DemoSurfaceTracker';
import { DemoHeartbeat } from './lib/DemoHeartbeat';

// Lazy-load pages for code splitting
const Login = React.lazy(() => import('./pages/Login'));
const Register = React.lazy(() => import('./pages/Register'));
const Onboarding = React.lazy(() => import('./pages/Onboarding'));
const DemoPicker = React.lazy(() => import('./pages/DemoPicker'));
const Waitlist = React.lazy(() => import('./pages/Waitlist'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));

// Bottom-nav pages
const AskCleoChat = React.lazy(() => import('./pages/AskCleoChat'));
const HorizonScreen = React.lazy(() => import('./pages/HorizonScreen'));
const ProfileScreen = React.lazy(() => import('./pages/ProfileScreen'));
const Updates = React.lazy(() => import('./pages/Updates'));
const UpdateCapture = React.lazy(() => import('./pages/UpdateCapture'));
const UpdatePostForm = React.lazy(() => import('./pages/UpdatePostForm'));
const SavedUpdates = React.lazy(() => import('./pages/SavedUpdates'));

// Full-screen pages
const CreateScreen = React.lazy(() => import('./pages/CreateScreen'));
const MatchingScreen = React.lazy(() => import('./pages/MatchingScreen'));
const RightNowCreate = React.lazy(() => import('./pages/RightNowCreate'));
const PublicEventCreate = React.lazy(() => import('./pages/PublicEventCreate'));
const NoVenueAccess = React.lazy(() => import('./pages/NoVenueAccess'));
const MapScreen = React.lazy(() => import('./pages/MapScreen'));
const EventPage = React.lazy(() => import('./pages/EventPage'));
const EventsHub = React.lazy(() => import('./pages/EventsHub'));
const PublicEventDetail = React.lazy(() => import('./pages/PublicEventDetail'));
const NearbyUsers = React.lazy(() => import('./pages/NearbyUsers'));
const EventJoinedConfirmation = React.lazy(() => import('./pages/EventJoinedConfirmation'));
const RateEvent = React.lazy(() => import('./pages/RateEvent'));
const RoomsScreen = React.lazy(() => import('./pages/RoomsScreen'));
// Notifications page removed — redirects to /horizon
const Messages = React.lazy(() => import('./pages/Messages'));
const EventChat = React.lazy(() => import('./pages/EventChat'));
const DMChat = React.lazy(() => import('./pages/DMChat'));
const UserProfile = React.lazy(() => import('./pages/UserProfile'));
const OrgProfile = React.lazy(() => import('./pages/OrgProfile'));

// Venue pages
const VenueRegister = React.lazy(() => import('./pages/VenueRegister'));
const OrganizerRegister = React.lazy(() => import('./pages/OrganizerRegister'));
const VenueGuard = React.lazy(() => import('./components/venue/VenueGuard'));
const VenuePendingApproval = React.lazy(() => import('./pages/venue/VenuePendingApproval'));
const VenueDashboard = React.lazy(() => import('./pages/venue/VenueDashboard'));
const VenueProfile = React.lazy(() => import('./pages/venue/VenueProfile'));
const VenueStaffManager = React.lazy(() => import('./pages/venue/VenueStaffManager'));
const VenueEventList = React.lazy(() => import('./pages/venue/VenueEventList'));
const VenueNotices = React.lazy(() => import('./pages/venue/VenueNotices'));
const VenueEventCreate = React.lazy(() => import('./pages/venue/VenueEventCreate'));
const OrgMemberList = React.lazy(() => import('./pages/venue/OrgMemberList'));
const OrgSettings = React.lazy(() => import('./pages/venue/OrgSettings'));

function PrivateRoute({ children }: { children?: React.ReactNode }): React.ReactElement {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isHydrated = useAuthStore((s) => s.isHydrated);

  if (!isHydrated) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/login" replace />;

  return <>{children ?? <Outlet />}</>;
}

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <React.Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      }
    >
      {children}
    </React.Suspense>
  );
}

// iPhone 15 Pro dimensions
const PHONE_W = 393;
const PHONE_H = 852;
const BEZEL = 28;
const TOTAL_W = PHONE_W + BEZEL + 32;
const TOTAL_H = PHONE_H + BEZEL;

/** Reusable phone bezel + screen shell */
function PhoneFrame({ children, scale }: { children: React.ReactNode; scale: number }) {
  return (
    <div style={{ width: PHONE_W * scale, height: PHONE_H * scale }}>
      <div className="relative" style={{ width: PHONE_W, height: PHONE_H, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
        {/* Outer bezel */}
        <div
          className="absolute rounded-[54px] border-[3px] border-[#2a2a2a] shadow-2xl"
          style={{
            inset: -14,
            background: 'linear-gradient(145deg, #1c1c1e 0%, #0d0d0d 50%, #1c1c1e 100%)',
            boxShadow: '0 25px 60px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)',
          }}
        />
        {/* Side buttons — left */}
        <div className="absolute -left-[16px] top-[120px] w-[3px] h-[30px] rounded-l-sm bg-[#2a2a2a]" />
        <div className="absolute -left-[16px] top-[170px] w-[3px] h-[55px] rounded-l-sm bg-[#2a2a2a]" />
        <div className="absolute -left-[16px] top-[235px] w-[3px] h-[55px] rounded-l-sm bg-[#2a2a2a]" />
        {/* Side button — right (power) */}
        <div className="absolute -right-[16px] top-[190px] w-[3px] h-[75px] rounded-r-sm bg-[#2a2a2a]" />
        {/* Screen area */}
        <div
          className="relative w-full h-full rounded-[44px] overflow-hidden bg-background text-foreground"
          style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)' }}
        >
          {/* Notch safe-area clipper — sits just below the Dynamic Island
              and obscures anything scrolling into the top 52px of the
              phone screen. Without this, scrolled content (titles, tag
              chips, etc.) visually leaks around the notch on either side,
              which on a real iPhone is what safe-area-inset-top + a
              translucent NavigationBar would handle. Gradient fades to
              transparent over the last 16px so full-bleed hero images
              still scroll behind it cleanly without a hard line. */}
          <div
            className="absolute top-0 left-0 right-0 z-[9997] pointer-events-none"
            style={{
              height: 52,
              background: 'linear-gradient(to bottom, #050510 0%, #050510 70%, rgba(5,5,16,0) 100%)',
            }}
            aria-hidden="true"
          />
          {/* Dynamic Island */}
          <div className="absolute top-[11px] left-1/2 -translate-x-1/2 z-[9998]">
            <div className="bg-black rounded-[20px] flex items-center justify-center" style={{ width: 126, height: 37 }}>
              <div className="w-[12px] h-[12px] rounded-full bg-[#0a0a0a] ml-[40px]"
                style={{ boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.8), 0 0 2px rgba(50,50,80,0.3)' }}
              />
            </div>
          </div>
          {/* Home indicator */}
          <div className="absolute bottom-[8px] left-1/2 -translate-x-1/2 z-[9998]">
            <div className="w-[134px] h-[5px] rounded-full bg-white/20" />
          </div>
          {/* Content */}
          {children}
        </div>
      </div>
    </div>
  );
}

/** Phone-width shell — constrains the entire app to mobile proportions on desktop.
 *  Toggle the iPhone frame to preview the app in a realistic device shell.
 *  Dual mode renders a second phone via iframe on an alternate origin for P2P testing.
 *
 *  On REAL mobile devices (small viewport + touch-primary pointer), the shell
 *  is bypassed entirely — the app fills the actual screen edge-to-edge. The
 *  iPhone frame and 430px-wide column only make sense as desktop previews.
 */
/** Singleton Toaster scoped to whatever phone-bounded container PhoneShell
 *  is currently rendering. Lives at PhoneShell's child level so its <ol>
 *  positions against the bounded phone area instead of escaping to the
 *  desktop browser viewport. CSS overrides in globals.css convert sonner's
 *  default position:fixed to position:absolute and pin top to the phone
 *  shell's top edge — see .vesper-phone-toaster rules. */
function PhoneToaster(): React.ReactElement {
  return (
    <div className="vesper-phone-toaster">
      <Toaster
        position="top-center"
        richColors={false}
        toastOptions={{
          style: {
            background: 'rgba(15,15,28,0.95)',
            color: '#fff',
            border: '0.5px solid rgba(229,178,89,0.30)',
            backdropFilter: 'blur(12px)',
          },
        }}
      />
    </div>
  );
}

function PhoneShell({ children }: { children: React.ReactNode }) {
  // Dev-shell controls (iPhone Frame / Exit Frame / Dual-Single) are
  // super-organizer-only — they're a development convenience, not a real
  // user-facing feature. Demo and normal users never see them.
  const user = useAuthStore((s) => s.user);
  const isSuperOrganizer = user?.isSuperOrganizer === true;

  // When loaded inside an iframe (dual mode), skip all shell chrome and just render
  // the app with phone-height constraints so h-screen / nav work correctly.
  const isEmbedded = React.useMemo(() => {
    try { return new URLSearchParams(window.location.search).get('embedded') === '1'; } catch { return false; }
  }, []);

  // Real-mobile detection. `pointer: coarse` is touch-primary (excludes
  // desktop browsers shrunk to mobile dimensions for testing — those still
  // get the frame so devs can see what mobile users see). Reactive so
  // rotation / browser-resize flip the mode immediately.
  const [isRealMobile, setIsRealMobile] = React.useState(() => {
    try {
      return window.matchMedia('(max-width: 768px) and (pointer: coarse)').matches;
    } catch { return false; }
  });
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia('(max-width: 768px) and (pointer: coarse)');
    const onChange = (e: MediaQueryListEvent) => setIsRealMobile(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange); // Safari < 14 fallback
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);

  // Default the iPhone frame ON for desktop visitors (YC demo polish).
  // A super-organizer who hits "Exit Frame" sets '0' in localStorage and we
  // honor that. Everyone else gets the framed device on first load.
  const [showFrame, setShowFrame] = React.useState(() => {
    try {
      return localStorage.getItem('ark_iphone_frame') !== '0';
    } catch {
      return true;
    }
  });
  const [dualMode, setDualMode] = React.useState(() => {
    try { return localStorage.getItem('ark_dual_phone') === '1'; } catch { return false; }
  });

  const [scale, setScale] = React.useState(1);

  React.useEffect(() => {
    if (!showFrame) return;
    function calc() {
      const divisor = dualMode ? (TOTAL_W * 2 + 40) : TOTAL_W;
      const s = Math.min(
        (window.innerHeight - 40) / TOTAL_H,
        (window.innerWidth - 60) / divisor,
      );
      setScale(s);
    }
    calc();
    window.addEventListener('resize', calc);
    return () => window.removeEventListener('resize', calc);
  }, [showFrame, dualMode]);

  const toggleFrame = () => {
    setShowFrame((v) => {
      const next = !v;
      try { localStorage.setItem('ark_iphone_frame', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  const toggleDual = () => {
    setDualMode((v) => {
      const next = !v;
      try { localStorage.setItem('ark_dual_phone', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  // Alternate origin for the second phone (localhost ↔ 127.0.0.1 = separate localStorage)
  const altOrigin = React.useMemo(() => {
    const origin = window.location.origin;
    if (origin.includes('localhost')) return origin.replace('localhost', '127.0.0.1');
    if (origin.includes('127.0.0.1')) return origin.replace('127.0.0.1', 'localhost');
    return origin; // fallback — won't get separate storage but still renders
  }, []);

  // Real mobile — bypass the entire shell so the app fills the actual phone
  // viewport edge-to-edge, no fake frame, no 430px max-width column, no
  // toggle button. The shell is a desktop-preview affordance only.
  if (isRealMobile) {
    return (
      <div className="min-h-screen bg-background text-foreground vesper-phone-bounds">
        {children}
        <PhoneToaster />
      </div>
    );
  }

  // Embedded mode (inside dual-phone iframe) — no shell chrome, just the app with phone constraints
  if (isEmbedded) {
    return (
      <div
        className="w-full h-screen overflow-y-auto overflow-x-hidden scrollbar-none iphone-frame-content bg-background text-foreground vesper-phone-bounds"
        style={{ '--phone-height': `${PHONE_H}px` } as React.CSSProperties}
      >
        {children}
        <PhoneToaster />
      </div>
    );
  }

  if (!showFrame) {
    return (
      <div className="min-h-screen bg-black flex justify-center relative">
        {/* Dev shell toggle — hidden on viewports narrower than 700px so it
            never overlaps the page content (430px + sidebar room) and moved
            to bottom-right so it can't visually collide with anything that
            looks like a phone notch on tighter widths. */}
        {isSuperOrganizer && (
          <button
            onClick={toggleFrame}
            className="hidden min-[700px]:block fixed bottom-3 right-3 z-[9999] px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/50 hover:text-white text-[11px] font-medium transition-colors backdrop-blur-sm border border-white/10"
          >
            iPhone Frame
          </button>
        )}
        <div className="w-full max-w-[430px] min-h-screen bg-background text-foreground relative vesper-phone-bounds">
          {children}
          <PhoneToaster />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a2e] flex items-center justify-center relative overflow-hidden">
      {/* Dev shell controls — hidden below 900px (single phone needs ~430px,
          dual needs ~860px, plus margin). Anchored bottom-right so they
          can never visually overlap the iOS dynamic-island region of the
          framed phone. Toggle them off entirely with the "Exit Frame" pill
          to get a flat preview. */}
      {isSuperOrganizer && (
        <div className="hidden min-[900px]:flex fixed bottom-3 right-3 z-[9999] gap-2">
          {showFrame && (
            <button
              onClick={toggleDual}
              className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/50 hover:text-white text-[11px] font-medium transition-colors backdrop-blur-sm border border-white/10"
            >
              {dualMode ? 'Single' : 'Dual'}
            </button>
          )}
          <button
            onClick={toggleFrame}
            className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/50 hover:text-white text-[11px] font-medium transition-colors backdrop-blur-sm border border-white/10"
          >
            Exit Frame
          </button>
        </div>
      )}

      <div className={`flex items-center ${dualMode ? 'gap-6' : ''}`}>
        {/* Left phone — current app. The toaster lives outside the
            scrolling iphone-frame-content but inside PhoneFrame so it
            stays pinned to the visible top edge of the phone instead
            of scrolling away with the page content. */}
        <PhoneFrame scale={scale}>
          <div className="relative w-full h-full vesper-phone-bounds">
            <div
              className="w-full h-full overflow-y-auto overflow-x-hidden scrollbar-none iphone-frame-content"
              style={{ '--phone-height': `${PHONE_H}px` } as React.CSSProperties}
            >
              {children}
            </div>
            <PhoneToaster />
          </div>
        </PhoneFrame>

        {/* Right phone — iframe on alternate origin (separate auth/localStorage) */}
        {dualMode && (
          <PhoneFrame scale={scale}>
            <iframe
              src={`${altOrigin}?embedded=1`}
              className="w-full h-full border-none"
              allow="camera;microphone;geolocation"
              title="Vesper — User 2"
            />
          </PhoneFrame>
        )}
      </div>
    </div>
  );
}

export default function App(): React.ReactElement {
  const { isOnline } = useOnlineStatus();
  return (
    <ErrorBoundary>
    {!isOnline && <OfflineBanner />}
    <PhoneShell>
    <UploadIndicator />
    <DemoSurfaceTracker />
    <DemoHeartbeat />
    <SuspenseWrapper>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/demo" element={<DemoPicker />} />
        <Route path="/waitlist" element={<Waitlist />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/org/:id" element={<OrgProfile />} />

        {/* Protected routes with bottom nav layout — Vesper 5-tab structure */}
        <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
          <Route path="cleo" element={<AskCleoChat />} />
          <Route path="horizon" element={<HorizonScreen />} />
          <Route path="rooms" element={<RoomsScreen />} />
          <Route path="updates" element={<Updates />} />
          <Route path="map" element={<MapScreen />} />
          <Route path="you" element={<ProfileScreen />} />

          {/* Legacy aliases */}
          <Route path="profile" element={<Navigate to="/you" replace />} />
          <Route path="ark" element={<Navigate to="/cleo" replace />} />
          <Route path="discover" element={<Navigate to="/horizon" replace />} />
          <Route path="connections" element={<Navigate to="/rooms" replace />} />
        </Route>

        {/* Protected routes without bottom nav (full-screen flows) */}
        <Route element={<PrivateRoute />}>
          {/* Update post creation: capture entry → compose form */}
          <Route path="updates/post" element={<UpdateCapture />} />
          <Route path="updates/compose" element={<UpdatePostForm />} />
          {/* Saved Updates — vertical feed of viewer's saves, opened from /you */}
          <Route path="you/saved" element={<SavedUpdates />} />

          {/* Create routes */}
          <Route path="create" element={<Navigate to="/create/hub" replace />} />
          <Route path="create/hub" element={<CreateScreen />} />
          {/* Phase 10 cleanup: /create/askark was a duplicate of /cleo.
              Matching screen URL retained for notification deep-links. */}
          <Route path="create/askark" element={<Navigate to="/cleo" replace />} />
          <Route path="create/askark/matching" element={<MatchingScreen />} />
          <Route path="create/rightnow" element={<RightNowCreate />} />
          <Route path="create/event" element={<PublicEventCreate />} />
          <Route path="create/public-event-access" element={<NoVenueAccess />} />

          {/* Events */}
          <Route path="events" element={<EventsHub />} />
          <Route path="events/:id" element={<EventPage />} />
          <Route path="events/:id/joined" element={<EventJoinedConfirmation />} />
          <Route path="events/:id/rate" element={<RateEvent />} />
          <Route path="public-events/:id" element={<PublicEventDetail />} />

          {/* Horizon sub-pages */}
          <Route path="horizon/nearby" element={<NearbyUsers />} />
          <Route path="discover/nearby" element={<Navigate to="/horizon/nearby" replace />} />

          {/* Social */}
          <Route path="bench" element={<Navigate to="/rooms" replace />} />
          {/* Phase 3 deferral: editorial Horizon doesn't read `tab` param.
              Routing notifications back to /horizon for now; Phase 10 follow-up
              will land notifications in a top-bar bell drawer. */}
          <Route path="notifications" element={<Navigate to="/horizon" replace />} />
          <Route path="messages" element={<Messages />} />
          <Route path="messages/:eventId" element={<EventChat />} />
          <Route path="messages/dm/:conversationId" element={<DMChat />} />
          <Route path="users/:id" element={<UserProfile />} />

          {/* Registration */}
          <Route path="register-venue" element={<VenueRegister />} />
          <Route path="register-organizer" element={<OrganizerRegister />} />
        </Route>

        {/* Venue routes — VenueGuard checks access + approval */}
        <Route element={<PrivateRoute />}>
          <Route path="venue/pending" element={<VenuePendingApproval />} />
          <Route element={<VenueGuard />}>
            <Route path="venue" element={<VenueDashboard />} />
            <Route path="venue/:venueId" element={<VenueDashboard />} />
            <Route path="venue/:venueId/profile" element={<VenueProfile />} />
            <Route path="venue/:venueId/events" element={<VenueEventList />} />
            <Route path="venue/:venueId/events/create" element={<VenueEventCreate />} />
            <Route path="venue/:venueId/notices" element={<VenueNotices />} />
            <Route path="venue/:venueId/members" element={<OrgMemberList />} />
            <Route element={<VenueGuard requireOwner />}>
              <Route path="venue/:venueId/staff" element={<VenueStaffManager />} />
              <Route path="venue/:venueId/org-settings" element={<OrgSettings />} />
            </Route>
          </Route>
        </Route>

        {/* Default route — Vesper home is Cleo */}
        <Route path="/" element={<Navigate to="/cleo" replace />} />

        {/* 404 fallback */}
        <Route
          path="*"
          element={
            <div className="flex flex-col items-center justify-center min-h-screen text-center p-4">
              <h1 className="text-5xl font-extrabold text-foreground mb-3">404</h1>
              <p className="text-muted-foreground mb-6">Page not found</p>
              <a href="/cleo" className="text-primary font-semibold text-sm hover:underline">
                Go home
              </a>
            </div>
          }
        />
      </Routes>
    </SuspenseWrapper>
    </PhoneShell>
    </ErrorBoundary>
  );
}
